import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { OrderItem } from '../../../types';
import { useOrderShareLink, useRevokeOrderShare } from '../../../api/orderApi';
import { copyText } from '../../../utils/clipboard';
import { paymentStatusLabel, pendingBalanceOf } from '../../../utils/paymentAmount';

interface Props {
  order: OrderItem;
  onClose: () => void;
}

/** 摘要文字与分享链接都由服务端产出（POST /orders/:id/share），这里只负责展示与复制 */
function fmtDate(d: string | null): string {
  if (!d) return '-';
  const [y, m, day] = d.split('-');
  return y && m && day ? `${y}年${m}月${day}日` : d;
}

const STATUS_COLORS: Record<string, string> = {
  '未执行': 'bg-amber-50 text-amber-700 border-amber-200',
  '执行中': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  '已完成': 'bg-teal-50 text-teal-700 border-teal-200',
  '已取消': 'bg-rose-50 text-rose-600 border-rose-200',
  '已归档': 'bg-gray-100 text-gray-500 border-gray-200',
};

export default function ShareOrderModal({ order, onClose }: Props) {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState<'text' | 'link' | null>(null);
  const [revoked, setRevoked] = useState(false);
  const revokedRef = useRef(false);
  const shareLink = useOrderShareLink(order.id);
  const revokeShare = useRevokeOrderShare();

  const share = shareLink.data;
  const summaryText = share?.summaryText ?? '';
  // 与后端 buildOrderSummary 同口径：0 元单对外是「无金额」，不是「未付款 + 零待收」
  const payStatus = paymentStatusLabel(order.paymentStatus ?? '-', order.orderAmount);
  const balance = pendingBalanceOf(Number(order.orderAmount ?? 0), Number(order.paidAmount ?? 0));
  const statusCls = STATUS_COLORS[order.orderStatus ?? ''] ?? 'bg-gray-100 text-gray-500 border-gray-200';

  // 撤销后关掉的弹窗要把这条作废链接从缓存摘掉（staleTime 是 Infinity，不会自己重取），
  // 否则下次打开显示的是「看着有效、点开链接无效」的死链接
  useEffect(
    () => () => {
      if (revokedRef.current) queryClient.removeQueries({ queryKey: ['order-share', order.id] });
    },
    [order.id, queryClient],
  );

  const flash = (which: 'text' | 'link') => {
    setCopied(which);
    setTimeout(() => setCopied(null), 2500);
  };

  const handleCopy = async (text: string, which: 'text' | 'link') => {
    try {
      await copyText(text);
      flash(which);
    } catch (e) {
      alert(e instanceof Error && e.message ? e.message : '复制失败，请手动选中复制');
    }
  };

  const handleRevoke = async () => {
    try {
      await revokeShare.mutateAsync(order.id);
      revokedRef.current = true;
      setRevoked(true);
    } catch (e) {
      // 撤销失败却提示成功，用户会以为公开入口已经关掉，是最坏的一种误导
      alert(e instanceof Error && e.message ? e.message : '撤销失败，请稍后重试');
    }
  };

  const handleRegenerate = async () => {
    revokedRef.current = false;
    setRevoked(false);
    await shareLink.refetch();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg">
              <i className="ri-share-line text-teal-600 text-sm" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">分享订单</p>
              <p className="text-xs text-gray-400 font-mono">{order.orderNumber ?? order.id.slice(0, 8).toUpperCase()}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer transition-colors rounded-lg hover:bg-gray-50">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Order summary card */}
        <div className="px-5 pt-4 shrink-0">
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <p className="text-xs text-gray-400 mb-0.5">服务对象</p>
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {order.customer?.fullName ?? order.contactPerson ?? '未知客户'}
                </p>
                {order.customer?.companyName && (
                  <p className="text-xs text-gray-400 truncate">{order.customer.companyName}</p>
                )}
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full border font-medium shrink-0 whitespace-nowrap ${statusCls}`}>
                {order.orderStatus}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {order.serviceType?.name && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <i className="ri-camera-line text-gray-400 text-xs" />
                  {order.serviceType.name}
                </div>
              )}
              {order.orderStartDate && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <i className="ri-calendar-line text-gray-400 text-xs" />
                  {fmtDate(order.orderStartDate)}
                </div>
              )}
              {order.contactPhone && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <i className="ri-phone-line text-gray-400 text-xs" />
                  {order.contactPhone}
                </div>
              )}
              {order.orderAmount != null && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <i className="ri-money-cny-circle-line text-gray-400 text-xs" />
                  ¥{Number(order.orderAmount).toLocaleString('zh-CN')}
                  {balance > 0 && <span className="text-amber-500">（余 ¥{balance.toLocaleString('zh-CN')}）</span>}
                </div>
              )}
              <div className="flex items-center gap-1.5 text-gray-600">
                <i className="ri-bank-line text-gray-400 text-xs" />
                {payStatus}
              </div>
            </div>
          </div>
        </div>

        {/* Share link */}
        <div className="px-5 pt-3 shrink-0">
          {shareLink.isPending && (
            <div className="flex items-center gap-2 text-xs text-gray-400 py-3">
              <i className="ri-loader-4-line animate-spin" />
              正在生成分享链接…
            </div>
          )}

          {shareLink.isError && (
            <div className="bg-rose-50 border border-rose-100 rounded-xl p-3 flex items-start justify-between gap-3">
              <p className="text-xs text-rose-600 leading-relaxed">
                {shareLink.error instanceof Error && shareLink.error.message
                  ? shareLink.error.message
                  : '分享链接生成失败'}
              </p>
              <button
                onClick={() => shareLink.refetch()}
                className="text-xs px-2.5 py-1 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-100 cursor-pointer whitespace-nowrap shrink-0"
              >
                重试
              </button>
            </div>
          )}

          {share && !shareLink.isPending && (
            <div className={`rounded-xl border p-3 ${revoked ? 'bg-gray-50 border-gray-200' : 'bg-teal-50/50 border-teal-100'}`}>
              <p className="text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <i className="ri-link text-xs" />
                分享链接
                {revoked && <span className="text-rose-500">（已撤销，打开会提示链接无效）</span>}
              </p>
              <p className={`text-xs break-all leading-relaxed ${revoked ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                {share.shareUrl}
              </p>
              <div className="flex items-center justify-between gap-2 mt-2.5">
                <span className="text-[11px] text-gray-400">
                  {new Date(share.expiresAt).toLocaleString('zh-CN')} 后自动失效
                </span>
                {revoked ? (
                  <button
                    onClick={handleRegenerate}
                    className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-white cursor-pointer whitespace-nowrap"
                  >
                    重新生成
                  </button>
                ) : (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={handleRevoke}
                      disabled={revokeShare.isPending}
                      className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-white cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      撤销
                    </button>
                    <button
                      onClick={() => handleCopy(share.shareUrl, 'link')}
                      className={`text-xs px-2.5 py-1 rounded-lg cursor-pointer whitespace-nowrap ${
                        copied === 'link' ? 'bg-teal-500 text-white' : 'bg-white border border-teal-200 text-teal-600 hover:bg-teal-50'
                      }`}
                    >
                      {copied === 'link' ? '已复制' : '复制链接'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Text preview */}
        <div className="px-5 pt-3 pb-2 shrink-0">
          <p className="text-xs text-gray-400 mb-1.5 flex items-center gap-1">
            <i className="ri-file-text-line text-xs" />
            可发送文字摘要
          </p>
        </div>
        <div className="px-5 flex-1 overflow-y-auto min-h-0">
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-3 font-mono text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
            {summaryText || '（链接生成成功后显示摘要）'}
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-t border-gray-100 shrink-0 space-y-2.5">
          <button
            onClick={() => handleCopy(summaryText, 'text')}
            disabled={!summaryText}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all whitespace-nowrap disabled:opacity-50 ${
              copied === 'text' ? 'bg-teal-500 text-white' : 'bg-teal-500 hover:bg-teal-600 text-white'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${copied === 'text' ? 'ri-check-line' : 'ri-clipboard-line'} text-sm`} />
            </div>
            {copied === 'text' ? '已复制到剪贴板！' : '复制文字摘要'}
          </button>

          <p className="text-xs text-center text-gray-400">
            摘要可直接粘贴到微信、短信；链接页只展示订单概要，不含审核与凭证信息
          </p>
        </div>
      </div>
    </div>
  );
}
